import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HighchartWidget from "../../components/highchartWidget";
import pieOptions from "../../components/charts/pieOptions";
import { setChartTitle } from "../../redux/slices/uiSlice";
import {
  selectActivePortfolioApiId,
  selectIsMainParentActive,
  selectMainAllocationPie,
} from "../../redux/slices/portfolioSlice";
import { formatAllocationPercent } from "../../services/portfolioAllocationMessages";

function resolveMainPiePayload(chartData, reduxPie, isMainParent, activeApiId) {
  if (!isMainParent || !activeApiId) {
    return null;
  }
  const candidate =
    chartData?.kind === "main_allocation_pie"
      ? chartData
      : reduxPie?.kind === "main_allocation_pie"
        ? reduxPie
        : null;
  if (!candidate) {
    return null;
  }
  if (
    candidate.portfolioApiId != null &&
    candidate.portfolioApiId !== activeApiId
  ) {
    return null;
  }
  return candidate;
}

function buildMainPieOptions(baseOptions, layout) {
  return {
    ...baseOptions,
    legend: { enabled: false },
    tooltip: {
      ...baseOptions.tooltip,
      pointFormat: "<b>{point.percentLabel}%</b>",
    },
    plotOptions: {
      ...baseOptions.plotOptions,
      pie: {
        ...(baseOptions.plotOptions?.pie || {}),
        size: layout.pieSize,
        showInLegend: false,
        dataLabels: {
          ...(baseOptions.plotOptions?.pie?.dataLabels || {}),
          distance: layout.isLandscape ? 10 : 14,
          format: "{point.percentLabel}%",
        },
      },
    },
  };
}

function enrichPiePoints(data) {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((pt) => {
    if (!pt || typeof pt !== "object") {
      return pt;
    }
    const y = Number(pt.y);
    const percentLabel = Number.isFinite(y)
      ? formatAllocationPercent(y)
      : "0";
    return { ...pt, percentLabel };
  });
}

export default function GraphContent({ chartData }) {
  const dispatch = useDispatch();
  const isMainParent = useSelector(selectIsMainParentActive);
  const activeApiId = useSelector(selectActivePortfolioApiId);
  const reduxPie = useSelector(selectMainAllocationPie);
  const [layout, setLayout] = useState(calcLayout());

  const mainPayload = useMemo(
    () => resolveMainPiePayload(chartData, reduxPie, isMainParent, activeApiId),
    [chartData, reduxPie, isMainParent, activeApiId]
  );

  const seriesModel = useMemo(() => {
    if (!mainPayload) {
      return null;
    }
    return {
      name: mainPayload.seriesName || "Composition",
      type: "pie",
      data: enrichPiePoints(mainPayload.data),
    };
  }, [mainPayload]);

  const absData = useMemo(
    () => (seriesModel ? toAbsolutePieSeries(seriesModel) : null),
    [seriesModel]
  );

  useEffect(() => {
    dispatch(setChartTitle(""));
  }, [dispatch, mainPayload]);

  useEffect(() => {
    const mm = window.matchMedia("(orientation: landscape)");
    const onChange = () => setLayout(calcLayout());
    onChange();

    if (mm.addEventListener) {
      mm.addEventListener("change", onChange);
    } else {
      mm.addListener(onChange);
    }
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);

    return () => {
      if (mm.removeEventListener) {
        mm.removeEventListener("change", onChange);
      } else {
        mm.removeListener(onChange);
      }
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, []);

  const mergedPieOptions = useMemo(
    () => buildMainPieOptions(pieOptions, layout),
    [layout]
  );

  if (!isMainParent || !mainPayload) {
    return (
      <div className="chart-content-center chart-content-center--empty">
        <p className="main-allocation-empty">No chart to display.</p>
      </div>
    );
  }

  const hasSlices = (absData?.data?.length ?? 0) > 0;
  const notice = mainPayload.partialNotice || null;

  return (
    <div className="chart-content-center main-allocation-wrap">
      {notice ? (
        <p className="main-allocation-notice" role="status">
          {notice}
        </p>
      ) : null}
      {hasSlices ? (
        <div
          key={`${layout.isLandscape}-${Math.round(layout.chartH)}`}
          className="main-allocation-chart"
          style={{
            width: "100%",
            maxWidth: 960,
            height: layout.chartH,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <HighchartWidget
            key={`pie-${activeApiId || ""}-${mainPayload.seriesName || ""}`}
            type="pie"
            height={layout.chartH}
            series={[
              { name: absData.name || "Data", data: absData.data || [] },
            ]}
            options={mergedPieOptions}
          />
        </div>
      ) : (
        <div
          className="main-allocation-chart main-allocation-chart--empty"
          style={{ minHeight: 120 }}
        />
      )}
      {hasSlices && mainPayload.legendItems?.length > 0 ? (
        <ul className="main-allocation-legend" aria-label="Composition legend">
          {mainPayload.legendItems.map((item) => (
            <li key={item.id || item.name} className="main-allocation-legend-item">
              <span
                className="main-allocation-legend-swatch"
                style={{ backgroundColor: item.color }}
                aria-hidden
              />
              <span className="main-allocation-legend-label">
                {item.name}{" "}
                <span className="main-allocation-legend-pct">
                  {item.percentLabel}%
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function toAbsolutePieSeries(series) {
  if (!series || !Array.isArray(series.data)) return series;
  const absData = series.data.map((pt) => {
    if (typeof pt === "number") return Math.abs(pt) || 0;
    if (Array.isArray(pt) && pt.length >= 2) {
      const [name, y, ...rest] = pt;
      const n = Number(y);
      return [name, Number.isFinite(n) ? Math.abs(n) : 0, ...rest];
    }
    if (pt && typeof pt === "object") {
      const n = Number(pt.y);
      return { ...pt, y: Number.isFinite(n) ? Math.abs(n) : 0 };
    }
    return pt;
  });
  return { ...series, data: absData };
}

function calcLayout() {
  const isLandscape = window.matchMedia("(orientation: landscape)").matches;

  const HEADER_H = isLandscape ? 40 : 52;
  const BOTTOM_H = isLandscape ? 0 : 96;
  const SIDEBAR_W = isLandscape ? 35 : 0;
  const PADDING_X = 32;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const availW = vw - SIDEBAR_W - PADDING_X;
  const availH = vh - HEADER_H - BOTTOM_H - 24;
  const chartH = clamp(
    isLandscape ? Math.min(availH * 0.85, 340) : Math.min(availH * 0.62, 480),
    220,
    availH
  );
  const pieSize = isLandscape
    ? availW < 560
      ? "58%"
      : "62%"
    : availW < 360
      ? "70%"
      : "78%";

  return { isLandscape, chartH, pieSize };
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}
